<template>
  <div :class="classes">
    <v-chip-group mandatory active-class="primary--text">
      <v-chip ref="chip" v-for="(group, idx) in chipGroups" :key="idx">
        <v-menu close-on-click offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn small tile ref="button" v-bind="attrs" v-on="on" @click="selectGroup(idx)">
              <v-icon small>{{ groupIcon(idx, group) }}</v-icon>
              <span style="margin-left: 6px">{{ idx === 0 ? $t('system.select') : group.name }}</span>
            </v-btn>
          </template>
          <div class="border-wrap">
            <v-list v-show="showMenu" :value="idx" dense>
              <v-list-item class="group-menu" v-for="(item, index) in group.children" :key="index" @click="selectMenu(index, item)" :disabled="item.selected">
                <v-list-item-title>{{ item.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </div>
        </v-menu>
      </v-chip>
    </v-chip-group>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import NodePath from '@/utils/node-path';
// import bus from '@/utils/bus';

export default {
  data() {
    return {
      selectedGroupIndex: 0,
      chipGroups: [],
      path: [],
      showMenu: true,
      nodePath: null,
      prevPath: null,
    };
  },
  computed: {
    ...mapState('common/system', ['system']),
    ...mapState('common/group', ['groups']),
    ...mapState('common/user', ['defaultTargetMonitoringSystemId']),
    ...mapGetters('common/system', ['getSystemById']),
    classes() {
      return {
        'hidden-group-selector': this.$route.meta.hiddenGroupSelector || false,
        'float-left': true,
        'd-inline-block': true,
        'menu-bar-left': true,
        'group-chip': true,
      };
    },
  },
  watch: {
    $route: {
      handler: 'onChangeRouteHandler',
    },
  },
  methods: {
    onChangeRouteHandler() {
      if (this.prevPath != this.$route.meta.path) {
        this.prevPath = this.$route.meta.path;
        this.chipGroups = [];
        this.addGroup({
          name: this.$t('system.select'),
          children: this.groups,
        });
        this.nodePath = new NodePath(this.groups, { key: 'systemId', value: this.system.id }).find().computeFirstNode();
        this.nodePath.firstNode?.path?.group.map(item => {
          this.chipGroups.push(item);
          this.setMenuDisabled(item);
        });
      }
    },
    /**
     * 선택된 item을 다음 chip group으로 추가하여 준다.
     * Commented.by.KWShin.2024.07.09.
     */
    selectMenu(index, item) {
      this.chipGroups.slice(this.selectedGroupIndex + 1).forEach(() => {
        const targetIndex = this.selectedGroupIndex + 1;
        this.chipGroups.splice(targetIndex, 1);
      });
      const routeStackType = this.$route.meta.stackType;
      this.chipGroups[this.selectedGroupIndex].children.forEach(child => {
        if (child.systemId) {
          const sstmStackType = this.getSystemById(child.systemId).stackType;
          if (routeStackType != 'ALL' && sstmStackType != 'DUAL' && routeStackType != sstmStackType) child.selected = true;
          else child.selected = false;
        } else {
          child.selected = false;
        }
      });
      this.addGroup(item);
    },
    /**
     * chipGroups에 group을 추가하고,
     * 추가된 그룹이 화면에 반영되면 마지막 단계의 그룹을 선택해준다.
     */
    addGroup(group) {
      this.chipGroups.push(group);
      this.setMenuDisabled(group);
      this.$nextTick(() => {
        if (this.chipGroups.length == this.$refs.button.length) {
          this.selectGroup(this.chipGroups.length - 1);
        }
      });
    },
    /**
     * 선택된 그룹의 자식 목록을 메뉴에 보여주고,
     * 선택된 그룹의 style을 바꾸어 주고,
     * 선택된 것이 그룹이 아니라 시스템이면 route와 store 정보 변경
     */
    async selectGroup(index) {
      this.selectedGroupIndex = index;
      this.showMenu = (this.chipGroups[index].children || []).length > 0;

      // 칩을 클릭해주는 이유는 칩이 선택되었다고 style이 바뀌게 하기 위함이다.
      this.$refs.chip[index].click();

      // 최종으로 systemId 를 선택했을 경우
      if (this.chipGroups[index].systemId) {
        this.path = this.nodePath.findPath(this.chipGroups[index], this.groups).group;
        /*
         * systemId 가 변경되었을 경우 route와 system 정보 변경
         */
        if (this.system.id != this.chipGroups[index].systemId) {
          const selectedSstm = this.chipGroups[index].systemId;
          this.prevPath = this.$route.meta.path;
          this.$router.push(`${this.$route.meta.path}/${selectedSstm}`);
          this.$store.dispatch('common/system/setSystem', selectedSstm);
        }
      }
    },
    /**
     * 이 컴포넌트가 created() 된 후 호출된다. 즉, 브라우저 URL 창에서 엔터를 쳐도 여기에 걸린다. 페이지가 릴로드되니까...
     * this.groups에는 모든 등록된 시스템그룹 트리 정보가 다 들어있다.
     * 물론, 서버에서 하위에 등록된 정보(시스템그룹 또는 시스템)가 없는 시스템그룹이나 사용자의 모니터링 권한이 없는 시스템은 필터링 해준다.
     * 디폴트로 route에 딸려온 시스템아이디나 사용자의 디폴트 모니터링 시스템의 아이디를 선택해준다.
     * 이 컴포넌트가 초기화될 때 첫번째 최상위 그룹의 최종 후손까지 chip group이 그려지는 이유는
     * this.nodePath를 만들어주면서 최종 후손까지 chip group을 만들어주기 때문이다.
     * Commented.by.KWShin.2024.07.06.
     */
    setGroupPath() {
      this.chipGroups = [];
      this.addGroup({
        name: this.$t('system.select'),
        children: this.groups,
      });
      // 다른 화면에서 시스템ID를 물고 연결되어 들어오면 그 시스템ID를
      // 그렇지 않으면 path variable을 그것도 아니면 사용자의 디폴트 모니터링 시스템을 선택하여 준다.
      const systemId = this.$route.query.sid ? this.$route.query.sid : this.$route.params.systemId || this.defaultTargetMonitoringSystemId;
      // 바뀐 시스템을 현재 시스템으로 Store에 저장.
      if (this.system.id != systemId) this.$store.dispatch('common/system/setSystem', systemId);
      // 바뀐 시스템을 가진 첫번째 그룹을 선택.
      this.nodePath = new NodePath(this.groups, { key: 'systemId', value: systemId }).find().computeFirstNode();
      this.nodePath.firstNode?.path?.group.map(item => {
        this.chipGroups.push(item);
        this.setMenuDisabled(item);
      });
      this.prevPath = this.$route.meta.path;
    },
    /**
     * 선택된 경로를 표시하고
     * 이미 선택된 시스템이나 stack type이 다른 시스템은 선택하지 못하게 하기 위해 경로를 passive 처리.
     * Added.by.KWShin.2024.07.15.
     */
    setMenuDisabled(group) {
      group.selected = true;
      group.children?.forEach(child => {
        if (child.systemId) {
          const sstm = this.getSystemById(child.systemId);
          const showSstm = sstm.stackType == 'DUAL' || this.$route.meta.stackType == 'ALL' || sstm.stackType == this.$route.meta.stackType;
          if (!showSstm || child.systemId == this.$route.params.systemId) child.selected = true;
          else child.selected = false;
        } else {
          child.selected = false;
        }
      });
    },
    groupIcon(index, group) {
      if (index == 0) return 'mdi-ungroup';
      else if (group.systemId) return 'mdi-monitor-screenshot';
      else return 'mdi-ungroup';
    },
  },
  created() {
    this.setGroupPath();
  },
};
</script>

<style>
.menu-bar-left {
  display: inline-block;
  width: 40%;
  position: absolute;
  height: 30px;
}
.menu-bar-left .v-breadcrumbs {
  padding: 0;
}
.menu-bar-left .v-breadcrumbs .v-breadcrumbs__divider {
  padding: 0;
}

.v-application p {
  margin-bottom: 0px;
}
.v-chip.v-size--small {
  border-radius: 5px;
}
.v-slide-group__prev,
.v-slide-group__prev--disabled {
  display: flex !important;
  min-width: 41px;
}
.v-slide-group__next,
.v-slide-group__next--disabled {
  display: flex !important;
  min-width: 41px;
}
</style>

<style scoped>
.group-chip .v-chip {
  border-radius: initial;
  margin: 0px;
  padding: 0px;
  height: 33px;
  background: none;
}
.hidden-group-selector {
  display: none !important;
}
</style>
